<template>
  <div class="page-layout">
    <div class="page-content">
      <AppBreadcrumbs :items="breadcrumbItems" />
      <div class="page-header-title">
        <UserAvatar v-if="author" :user="author" size="md" />
        <h1>{{ pageTitle }}</h1>
      </div>

      <div v-if="loading" class="panel">
        <p class="empty-state">Загрузка...</p>
      </div>

      <div v-else-if="error" class="panel">
        <p class="error-message">{{ error }}</p>
      </div>

      <div v-else class="panel">
        <TrackList
          title=""
          :tracks="tracks"
          empty-text="У автора пока нет публичных треков."
          @select="openTrack"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TrackList from '../components/TrackList.vue'
import UserAvatar from '../components/UserAvatar.vue'
import AppBreadcrumbs from '../components/AppBreadcrumbs.vue'
import { fetchUserPublicTracks, fetchUserPublicTracksByUsername } from '../api/tracks'
import { userDisplayName } from '../utils/userDisplay'
import { redirectToLoginIfUnauthorized } from '../utils/profileAccess'

const route = useRoute()
const router = useRouter()

const author = ref(null)
const tracks = ref([])
const loading = ref(true)
const error = ref('')

const pageTitle = computed(() => {
  if (author.value) {
    return `Публичные треки — ${userDisplayName(author.value)}`
  }
  return 'Публичные треки'
})

const breadcrumbItems = computed(() => {
  const items = [{ label: 'Треки', to: { name: 'home' } }]

  if (author.value) {
    if (author.value.username) {
      items.push({
        label: userDisplayName(author.value),
        to: { name: 'public-profile', params: { username: author.value.username } },
      })
    } else {
      items.push({ label: userDisplayName(author.value) })
    }
  }

  items.push({ label: 'Публичные треки' })
  return items
})

async function loadAuthorTracks() {
  loading.value = true
  error.value = ''
  author.value = null
  tracks.value = []
  let redirecting = false

  try {
    const response = route.params.username
      ? await fetchUserPublicTracksByUsername(route.params.username)
      : await fetchUserPublicTracks(route.params.id)
    author.value = response.data.author
    tracks.value = response.data.tracks
  } catch (err) {
    if (redirectToLoginIfUnauthorized(err, router, route)) {
      redirecting = true
      return
    }

    if (err.response?.status === 404) {
      error.value = route.params.username ? 'Профиль не найден или скрыт' : 'Автор не найден'
    } else {
      error.value = 'Не удалось загрузить треки автора'
    }
  } finally {
    if (!redirecting) {
      loading.value = false
    }
  }
}

function openTrack(id) {
  router.push({ name: 'track', params: { id } })
}

onMounted(loadAuthorTracks)
watch(() => [route.params.id, route.params.username], loadAuthorTracks)
</script>
